import { wrapRequest } from '@applandeo/api-wrapper';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ALERT_VARIANT, useAlertMessages } from '@/components/Global/AlertMessages/AlertMessagesProvider';
import { getOwnLimitsUsageRequestApiV3, getUserLimitsUsage } from '@/dataExchange/services/newRequest.service';
import { Limit, OwnLimitsUsageResponseV3 } from '@/domain/newRequest';
import useLoggedInUserData from '@/hooks/useUserData/useLoggedInUserData';
import { useSentryActions } from '@/sentry/hooks/useSentryActions';
import { SENTRY_ACTIONS } from '@/sentry/sentryActions';
import { normalizeDateForAPI } from '@/utils/date/date';
import { GET_LIMITS_USAGE_RANGE_TYPE } from '@/utils/enums/newRequest';

type LimitsUsageLogicProps = {
    userId?: string;
    includeArchived?: boolean;
};

/**
 * There are two similar enpoints to get used Limits but they are slightly different.
 * Please check and make sure it will cover your use case
 * @see {@link getOwnLimitsUsageRequest}
 */
const useLimitsUsageLogic = ({ userId, includeArchived }: LimitsUsageLogicProps) => {
    const getOwnLimitsUsageWrapper = wrapRequest(getOwnLimitsUsageRequestApiV3);
    const getUserLimitsUsageWrapper = wrapRequest(getUserLimitsUsage);

    const { t } = useTranslation();
    const { createAlert } = useAlertMessages();
    const { isAdmin } = useLoggedInUserData();
    const { handleSentryAction } = useSentryActions();

    const [limitsUsage, setLimitsUsage] = useState<Limit[] | OwnLimitsUsageResponseV3[]>();
    const getOwnLimitsUsage = useCallback(() => {
        getOwnLimitsUsageWrapper
            .fetch({ date: normalizeDateForAPI(new Date()), includeArchived })
            .onAnyError(error => {
                createAlert(ALERT_VARIANT.error, t('applicationPreview_fetchEmployeeLimits_error'));
                handleSentryAction(error, SENTRY_ACTIONS.ERROR_GET_OWN_LIMITS_USAGE);
            })
            .onSuccess(response => {
                setLimitsUsage(response.filter(Boolean));
            });
    }, [createAlert, getOwnLimitsUsageWrapper, t]);

    useEffect(() => {
        if (isAdmin) return;
        getOwnLimitsUsage();
    }, [isAdmin]);

    const getSelectedUserLimitsUsage = useCallback(() => {
        if (!userId) return;

        getUserLimitsUsageWrapper
            .fetch({ userId, rangeType: GET_LIMITS_USAGE_RANGE_TYPE.CURRENT_YEAR, includeArchived })
            .onAnyError(error => {
                createAlert(ALERT_VARIANT.error, t('applicationPreview_fetchEmployeeLimits_error'));
                handleSentryAction(error, SENTRY_ACTIONS.ERROR_GET_USER_LIMITS_USAGE);
            })
            .onSuccess(response => {
                const limitsUsage = response?.[0]?.buckets;
                setLimitsUsage(limitsUsage);
            });
    }, [createAlert, getUserLimitsUsageWrapper, t, userId]);

    useEffect(() => {
        if (!isAdmin) return;
        getSelectedUserLimitsUsage();
    }, [isAdmin, userId]);

    return { limitsUsage, isLoading: getOwnLimitsUsageWrapper?.isLoading };
};

export default useLimitsUsageLogic;
